import React, { useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from "recharts";
import { groupResultsByArea } from "./complementGrouping";

// Helper funkcia na získanie limitov podľa pohlavia
const getGenderLimits = (parameterDef, patientGender) => {
  if (parameterDef?.gender_limits?.length > 0 && patientGender) {
    const genderLimit = parameterDef.gender_limits.find(
      (gl) => gl.gender === patientGender,
    );
    if (genderLimit) {
      return {
        lower: parseFloat(genderLimit.lower_limit),
        upper: parseFloat(genderLimit.upper_limit),
      };
    }
  }
  return {
    lower: parameterDef?.lower_limit
      ? parseFloat(parameterDef.lower_limit)
      : null,
    upper: parameterDef?.upper_limit
      ? parseFloat(parameterDef.upper_limit)
      : null,
  };
};

// Helper funkcia na kontrolu či je hodnota mimo rozsah
const isValueOutOfRange = (value, limits) => {
  const numValue = parseFloat(value);
  if (isNaN(numValue) || (limits.lower === null && limits.upper === null)) {
    return false;
  }
  if (limits.lower !== null && numValue < limits.lower) {
    return true;
  }
  if (limits.upper !== null && numValue > limits.upper) {
    return true;
  }
  return false;
};

// Stabilný kľúč parametra pre stav výberu / rozbalenia (areaId + kľúč parametra).
const makeParamSelectionKey = (areaId, paramKey) =>
  `${areaId === null || areaId === undefined ? "none" : areaId}::${paramKey}`;

export const ComplementTrends = ({
  samplings,
  onCopyToClipboard,
  patientGender,
  areaOrderById = {},
  paramOrderByDefId = {},
  parameterDefinitionsById = {},
}) => {
  const [expandedAreas, setExpandedAreas] = useState({});
  const [selectedParameters, setSelectedParameters] = useState({});

  // Zoskupenie výsledkov podľa oborov priamo z dát (area / area_name na výsledku),
  // řazené obor -> parameter -> abeceda. Doplní limity a prepočet is_out_of_range.
  const buildAreas = () => {
    const allResults = (samplings || []).flatMap((sampling) =>
      (sampling.parameter_results || []).map((result) => ({
        ...result,
        made_at: sampling.made_at,
        sampling_id: sampling.id,
      })),
    );

    const areas = groupResultsByArea(allResults, {
      areaOrderById,
      paramOrderByDefId,
    });

    areas.forEach((area) => {
      area.parameters.forEach((parameter) => {
        const paramDef = parameterDefinitionsById[parameter.definitionId];
        const limits = getGenderLimits(paramDef, patientGender);
        parameter.limits = limits;

        if (paramDef && patientGender) {
          parameter.results = parameter.results.map((result) => ({
            ...result,
            is_out_of_range: isValueOutOfRange(result.value, limits),
            gender_limits: limits,
          }));
        }
      });
    });

    return areas;
  };

  const toggleArea = (areaKey) => {
    setExpandedAreas((prev) => ({
      ...prev,
      [areaKey]: !prev[areaKey],
    }));
  };

  const toggleParameterSelection = (selectionKey) => {
    setSelectedParameters((prev) => ({
      ...prev,
      [selectionKey]: !prev[selectionKey],
    }));
  };

  const areas = buildAreas();

  // Lookup vybraných parametrov podľa stabilného kľúča.
  const parametersBySelectionKey = {};
  areas.forEach((area) => {
    area.parameters.forEach((parameter) => {
      const selectionKey = makeParamSelectionKey(area.areaId, parameter.key);
      parametersBySelectionKey[selectionKey] = { area, parameter };
    });
  });

  const selectedEntries = Object.keys(selectedParameters)
    .filter((key) => selectedParameters[key] && parametersBySelectionKey[key])
    .map((key) => ({ key, ...parametersBySelectionKey[key] }));

  const copySelectedParameters = () => {
    const selectedResults = [];

    selectedEntries.forEach(({ parameter }) => {
      parameter.results.forEach((result) => {
        selectedResults.push({
          parameter_name: parameter.name,
          value: result.value,
          parameter_units: parameter.units,
          made_at: result.made_at,
        });
      });
    });

    if (selectedResults.length > 0) {
      onCopyToClipboard(selectedResults);
    }
  };

  const clearAllSelectedParameters = () => {
    setSelectedParameters({});
  };

  const hasSelection = selectedEntries.length > 0;

  return (
    <div className="complement-trends">
      <div className="trends-header">
        <div className="selected-actions">
          {hasSelection && (
            <>
              <button
                className="button btn-primary"
                onClick={copySelectedParameters}
              >
                Kopírovat vybrané parametry
              </button>
              <button
                className="button btn-light"
                onClick={clearAllSelectedParameters}
              >
                Zrušit výběr všech
              </button>
            </>
          )}
        </div>
      </div>

      <div className="trends-layout">
        {/* Ľavá strana - Tabulky */}
        <div className="trends-left">
          <div className="laboratory-areas">
            {areas.map((area) => {
              const areaKey =
                area.areaId === null || area.areaId === undefined
                  ? area.areaName
                  : area.areaId;

              return (
                <div key={areaKey} className="laboratory-area">
                  <div
                    className="area-header"
                    onClick={() => toggleArea(areaKey)}
                  >
                    <h3>{area.areaName}</h3>
                    <span
                      className={`expand-icon ${
                        expandedAreas[areaKey] ? "expanded" : ""
                      }`}
                    >
                      ▼
                    </span>
                  </div>

                  {expandedAreas[areaKey] && (
                    <div className="area-content">
                      <div className="parameters-grid">
                        {area.parameters.map((parameter) => {
                          const selectionKey = makeParamSelectionKey(
                            area.areaId,
                            parameter.key,
                          );

                          return (
                            <div
                              key={selectionKey}
                              className="parameter-section"
                            >
                              <div className="parameter-header">
                                <div className="parameter-info">
                                  <input
                                    type="checkbox"
                                    checked={
                                      selectedParameters[selectionKey] || false
                                    }
                                    onChange={() =>
                                      toggleParameterSelection(selectionKey)
                                    }
                                  />
                                  <span className="parameter-name">
                                    {parameter.name} ({parameter.abbreviation})
                                  </span>
                                </div>
                                <button
                                  className="button btn-sm"
                                  onClick={() => {
                                    const formattedResults =
                                      parameter.results.map((result) => ({
                                        parameter_name: parameter.name,
                                        value: result.value,
                                        parameter_units: parameter.units,
                                        made_at: result.made_at,
                                      }));
                                    onCopyToClipboard(formattedResults);
                                  }}
                                >
                                  Kopírovat
                                </button>
                              </div>

                              <div className="parameter-results-table">
                                <div className="table-header">
                                  <div className="header-cell">Datum</div>
                                  <div className="header-cell">Hodnota</div>
                                  <div className="header-cell">Jednotka</div>
                                </div>

                                <div className="table-body">
                                  {parameter.results
                                    .slice(0, 10)
                                    .map((result, index) => (
                                      <div
                                        key={`${result.sampling_id}_${result.id}_${index}`}
                                        className={`table-row ${
                                          result.is_out_of_range
                                            ? "out-of-range"
                                            : ""
                                        }`}
                                      >
                                        <div className="cell">
                                          {new Date(
                                            result.made_at,
                                          ).toLocaleDateString("cs-CZ")}
                                        </div>
                                        <div className="cell">
                                          {result.value}
                                        </div>
                                        <div className="cell">
                                          {parameter.units}
                                        </div>
                                      </div>
                                    ))}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Pravá strana - Grafy zaškrtnutých parametrov */}
        <div className="trends-right">
          <div className="charts-container">
            {selectedEntries.length > 0 ? (
              <>
                <h3 className="charts-title">Grafy vybraných parametrů</h3>
                {selectedEntries.map(({ key, area, parameter }) => (
                  <div key={key} className="parameter-chart">
                    <h4>
                      {parameter.name} ({parameter.abbreviation}) -{" "}
                      {area.areaName}
                    </h4>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart
                        data={parameter.results
                          .slice()
                          .reverse()
                          .map((result) => {
                            const numericValue = parseFloat(result.value);
                            return {
                              date: new Date(
                                result.made_at,
                              ).toLocaleDateString("cs-CZ"),
                              value: Number.isNaN(numericValue)
                                ? null
                                : numericValue,
                              isOutOfRange: result.is_out_of_range,
                            };
                          })}
                        margin={{
                          top: 20,
                          right: 30,
                          left: 20,
                          bottom: 20,
                        }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="date"
                          tick={{ fontSize: 12 }}
                          angle={-45}
                          textAnchor="end"
                          height={60}
                        />
                        <YAxis
                          tick={{ fontSize: 12 }}
                          label={{
                            value: parameter.units,
                            angle: -90,
                            position: "insideLeft",
                          }}
                        />
                        <Tooltip
                          formatter={(value) => [
                            value === null || value === undefined
                              ? "Nestanoveno"
                              : `${value} ${parameter.units}`,
                            parameter.name,
                          ]}
                          labelFormatter={(label) => `Datum: ${label}`}
                        />
                        {parameter.limits?.lower !== null &&
                          parameter.limits?.lower !== undefined && (
                            <ReferenceLine
                              y={parameter.limits.lower}
                              stroke="#f59e0b"
                              strokeDasharray="6 3"
                              label={{
                                value: `min: ${parameter.limits.lower}`,
                                position: "insideTopLeft",
                                fontSize: 11,
                                fill: "#f59e0b",
                              }}
                            />
                          )}
                        {parameter.limits?.upper !== null &&
                          parameter.limits?.upper !== undefined && (
                            <ReferenceLine
                              y={parameter.limits.upper}
                              stroke="#f59e0b"
                              strokeDasharray="6 3"
                              label={{
                                value: `max: ${parameter.limits.upper}`,
                                position: "insideBottomLeft",
                                fontSize: 11,
                                fill: "#f59e0b",
                              }}
                            />
                          )}
                        <Line
                          type="monotone"
                          dataKey="value"
                          stroke="#2563eb"
                          strokeWidth={2}
                          connectNulls
                          dot={(props) => {
                            const { cx, cy, payload, index } = props;
                            if (
                              payload.value === null ||
                              payload.value === undefined ||
                              cx == null ||
                              cy == null
                            ) {
                              return (
                                <circle
                                  key={`empty-dot-${index}`}
                                  r={0}
                                  fill="none"
                                />
                              );
                            }
                            return (
                              <circle
                                key={`dot-${index}`}
                                cx={cx}
                                cy={cy}
                                r={4}
                                fill={
                                  payload.isOutOfRange ? "#dc2626" : "#2563eb"
                                }
                                stroke={
                                  payload.isOutOfRange ? "#dc2626" : "#2563eb"
                                }
                                strokeWidth={2}
                              />
                            );
                          }}
                          activeDot={{ r: 6, fill: "#1d4ed8" }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                ))}
              </>
            ) : (
              <div className="no-charts-selected">
                <p>Vyberte parametry v levé části pro zobrazení grafů</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
